import React, { lazy } from 'react';
import { motion } from 'framer-motion';
import { QuoteIcon, StarIcon } from 'lucide-react';
import { testimoniosCopy, testimoniosExtra, testimonioAntesDespues, Testimonio } from '../data/content';
const all: Testimonio[] = [...testimoniosCopy, ...testimoniosExtra];
function initials(name: string) {
  return name.split(' ').map(n => n[0]).slice(0, 2).join('').toUpperCase();
}
export function TestimonialsSection() {
  return <section className="bg-cream-deep py-20 sm:py-28" aria-labelledby="testimonios-title" data-id="element-159">
      <div className="mx-auto max-w-5xl px-5" data-id="element-160">
        <div className="text-center" data-id="element-161">
          <div className="flex items-center justify-center gap-1 text-coral" data-id="element-162">
            {Array.from({
            length: 5
          }).map((_, i) => <StarIcon key={i} className="h-5 w-5 fill-current" aria-hidden="true" data-id="element-163" />)}
          </div>
          <h2 id="testimonios-title" className="mt-4 font-serif text-3xl font-semibold text-ink sm:text-4xl" data-id="element-164">
            Lo que dicen las mujeres que ya lo usan
          </h2>
        </div>

        <motion.figure initial={{
        opacity: 0,
        y: 20
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true,
        margin: '-60px'
      }} transition={{
        duration: 0.45
      }} className="mt-10 overflow-hidden rounded-[2rem] border border-sage/25 bg-white shadow-xl shadow-ink/5" data-id="element-165">
          <div className="grid lg:grid-cols-[1.1fr_0.9fr]" data-id="element-166">
            <div className="relative aspect-[3/2] overflow-hidden lg:aspect-auto" data-id="element-167">
              <img src={testimonioAntesDespues.image} alt="Julieta frente al espejo en dos momentos de su proceso personal de cambio de hábitos." className="h-full w-full object-cover" loading="lazy" data-id="element-168" />
              <div className="pointer-events-none absolute inset-x-0 bottom-0 flex justify-between px-4 pb-4 sm:px-6 sm:pb-6" data-id="element-169">
                <span className="rounded-full bg-ink/80 px-3 py-1.5 text-xs font-bold uppercase tracking-[0.16em] text-white" data-id="element-170">
                  {testimonioAntesDespues.beforeLabel}
                </span>
                <span className="rounded-full bg-sage-deep px-3 py-1.5 text-xs font-bold uppercase tracking-[0.16em] text-white" data-id="element-171">
                  {testimonioAntesDespues.afterLabel}
                </span>
              </div>
            </div>

            <div className="p-6 sm:p-8 lg:p-10" data-id="element-172">
              <p className="text-xs font-bold uppercase tracking-[0.2em] text-coral-deep" data-id="element-173">
                Un cambio de hábitos, paso a paso
              </p>
              <blockquote className="mt-3 font-serif text-2xl font-semibold leading-snug text-ink sm:text-3xl" data-id="element-174">
                “{testimonioAntesDespues.quote}”
              </blockquote>

              <div className="mt-6 grid gap-4 sm:grid-cols-2 lg:grid-cols-1" data-id="element-175">
                <div className="border-l-2 border-ink/20 pl-4" data-id="element-176">
                  <p className="text-xs font-bold uppercase tracking-widest text-ink/55" data-id="element-177">
                    {testimonioAntesDespues.beforeLabel}
                  </p>
                  <p className="mt-1 text-sm leading-relaxed text-ink/75" data-id="element-178">
                    {testimonioAntesDespues.before}
                  </p>
                </div>
                <div className="border-l-2 border-sage pl-4" data-id="element-179">
                  <p className="text-xs font-bold uppercase tracking-widest text-sage-deep" data-id="element-180">
                    {testimonioAntesDespues.afterLabel}
                  </p>
                  <p className="mt-1 text-sm leading-relaxed text-ink/75" data-id="element-181">
                    {testimonioAntesDespues.after}
                  </p>
                </div>
              </div>

              <figcaption className="mt-7 flex items-center gap-3 border-t border-sage/15 pt-5" data-id="element-182">
                <span className="flex h-11 w-11 items-center justify-center rounded-full bg-coral/15 font-serif text-sm font-semibold text-coral-deep" data-id="element-183">
                  {initials(testimonioAntesDespues.name)}
                </span>
                <span data-id="element-184">
                  <span className="block text-sm font-semibold text-ink" data-id="element-185">
                    {testimonioAntesDespues.name}
                  </span>
                  <span className="block text-xs text-ink/60" data-id="element-186">
                    {testimonioAntesDespues.location} ·{' '}
                    {testimonioAntesDespues.age} años
                  </span>
                </span>
              </figcaption>
              <p className="mt-4 rounded-xl bg-cream px-4 py-3 text-xs leading-relaxed text-ink/60" data-id="element-187">
                {testimonioAntesDespues.note}
              </p>
            </div>
          </div>
        </motion.figure>

        <div className="mt-12 columns-1 gap-6 sm:columns-2 [&>*]:mb-6" data-id="element-188">
          {all.map((t, i) => <motion.figure key={t.name + i} initial={{
          opacity: 0,
          y: 24
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true,
          margin: '-60px'
        }} transition={{
          duration: 0.45,
          delay: i % 2 * 0.06
        }} className="break-inside-avoid rounded-3xl bg-white p-6 shadow-sm" data-id="element-189">
              <QuoteIcon className="h-7 w-7 text-sage/50" aria-hidden="true" data-id="element-190" />
              <blockquote className="mt-3 text-[15px] leading-relaxed text-ink/80" data-id="element-191">
                "{t.quote}"
              </blockquote>
              <figcaption className="mt-5 flex items-center gap-3" data-id="element-192">
                <span className="flex h-11 w-11 items-center justify-center rounded-full bg-sage/20 font-serif text-sm font-semibold text-sage-deep" data-id="element-193">
                  {initials(t.name)}
                </span>
                <span data-id="element-194">
                  <span className="block text-sm font-semibold text-ink" data-id="element-195">
                    {t.name}
                  </span>
                  <span className="block text-xs text-ink/60" data-id="element-196">
                    {t.location} · {t.age} años
                  </span>
                </span>
              </figcaption>
            </motion.figure>)}
        </div>
      </div>
    </section>;
}